export const elementTypes = {
    html: 'Html',
    text: 'Text',
    audio: 'Audio',
    video: 'Video',
    image: 'Image',
    file: 'File',
    link: 'Link',
    form: 'Form',
    script: 'Script',
    reference: 'Reference',
};

export const linkTypes = {
    internal: 'Internal',
    task: 'Task',
    module: 'Module',
    program: 'Program',
    file: 'File',
    url: 'ExternalPage',
    youtube: 'Youtube',
    vimeo: 'Vimeo',
    audio: 'Audio',
    video: 'Video',
    image: 'Image',
};

export const formAnswerTypes = {
    freetext: 'Freetext',
    singleChoice: 'SingleChoice',
    multipleChoice: 'MultipleChoice',
    scale: 'Scale',
    table: 'Table',
    switch: 'Switch',
    action: 'Action',
};

export const appRoles = {
    portalAdmin: 'PortalAdmin',
    organizationAdmin: 'OrganizationAdmin',
    patientAdmin: 'PatientAdmin',
    programAdmin: 'ProgramAdmin',
    userAdmin: 'UserAdmin',
    therapistAndCreator: 'TherapistAndCreator',
    therapist: 'Therapist',
    contentCreator: 'ContentCreator',
    user: 'User',
    contactPerson: 'ContactPerson',
    none: 'None',

    isTherapist: roles => roles?.reduce((prev, r) => prev
        || r === 'Therapist' || r === 'TherapistAndCreator' || r === 'PatientAdmin' || r === 'OrganizationAdmin' || r === 'PortalAdmin'
    , false),
    isEditor: roles => roles?.reduce((prev, r) => prev
        || r === 'ContentCreator' || r === 'TherapistAndCreator' || r === 'ProgramAdmin' || r === 'OrganizationAdmin' || r === 'PortalAdmin'
    , false),
    // eslint-disable-next-line no-use-before-define
    isLimitedPatientAccess: role => !(appRoleLevels[role] >= appRoleLevels[appRoles.therapist]),
};

export const appRoleLevels = {
    [appRoles.portalAdmin]: 100,
    [appRoles.organizationAdmin]: 90,
    [appRoles.patientAdmin]: 60,
    [appRoles.programAdmin]: 50,
    [appRoles.userAdmin]: 40,
    [appRoles.therapistAndCreator]: 35,
    [appRoles.therapist]: 30,
    [appRoles.contentCreator]: 25,
    [appRoles.user]: 20,
    [appRoles.contactPerson]: 10,
};

export const elementSizes = {
    default: 'Default',
    small: 'Small',
    medium: 'Medium',
    large: 'Large',
};

export const loginLevels = {
    none: 0,
    key: 1,
    password: 2,
    twoFactor: 3,
    level4: 4,
};

export const patientUserLevels = {
    none: 'None',
    anonymous: 'Anonymous',
    person: 'Person',
    patient: 'Patient',
};

export const systemElementGroups = {
    main: 'Main',
    default: 'Default',
    app: 'App',
    therapist: 'Therapist',
    scheduled: 'Scheduled',
    consent: 'Consent',
    appointment: 'Appointment',
};

export const scheduleRepeatPeriod = {
    day: 'Day',
    week: 'Week',
    month: 'Month',
};

export const accessRoles = {
    none: 'None',
    read: 'Read',
    write: 'Write',
    owner: 'Owner',
    admin: 'Admin',
    isOwnPatient: role => role === 'Write' || role === 'Owner',
    hasWriteAccess: role => role === 'Write' || role === 'Owner' || role === 'Admin',
};

export const logoutReasons = {
    tokenExpired: '910',
    idleTimeout: '911',
    changeRole: '10101',
};

export const fileTypes = {
    file: 'File', // Any/Unknown..
    image: 'Image',
    audio: 'Audio',
    video: 'Video',
    document: 'Document',
};

export const elementConditionStates = {
    none: 'None',
    visible: 'Visible',
    hidden: 'Hidden',
    accepted: 'Accepted',
    rejected: 'Rejected',
    collapsed: 'Collapsed',
    expanded: 'Expanded',
};

export const sortDirection = {
    asc: 'asc',
    desc: 'desc',
};

export const sortDataType = {
    default: 'default',
    number: 'number',
    date: 'date',
};

export const patientFlagTypes = {
    criticalScore: 'CriticalScore',
    inactivePatient: 'InactivePatient',
    messageFollowup: 'MessageFollowup',
    responseFollowup: 'ResponseFollowup',
};

export const sensorTypes = {
    unknown: 'Unknown',
    fitbit: 'Fitbit',
    garmin: 'Garmin',
};

export const IdProviders = {
    youwell: 'Youwell',
    idPorten: 'IdPorten',
};

export const loginTypes = {
    password: 'Password',
    idPorten: 'IdPorten',
    twoFactor: 'twoFactor',
    selfStart: 'SelfStart',
};

export const freetextInputTypes = {
    default: 'Default',
    singleLineText: 'SingleLineText',
    email: 'Email',
    phone: 'Phone',
    number: 'Number',
    date: 'Date',
    dateAndTime: 'DateAndTime',
    time: 'Time',
};

export const patientDialogChannels = {
    chat: 'YouwellChat',
    sms: 'Sms',
};

export const patientPatchOperations = {
    addGroupMembership: 'AddGroupMembership',
    removeGroupMembership: 'RemoveGroupMembership',
};

export const contentPatchOperations = {
    changeOwner: 'ChangeOwner',
};

export const systemContentElementTypes = {
    default: 'Default',
    questionnaire: 'Questionnaire',
    bullseye: 'BullsEye',
    card: 'Card',
    box: 'Box',
    quizResponse: 'QuizResponse',
    bubbleChoice: 'BubbleChoice',
    routeSelection: 'RouteSelection',
};

export const systemTaskLabels = {
    subtask: 'subtask',
};

export const thresholdTypes = {
    lessThan: 'LessThan',
    moreThan: 'MoreThan',
    equals: 'Equals',
    none: 'None',
};

export const workflowStatuses = {
    progress: 100,
    review: 300,
    ready: 500,

    parse: config => config?.map((item) => {
        const kv = item.split(':');
        return {
            id: Number(kv[0]),
            name: kv[1],
        };
    }) || [],
};

export const contentElemenIdPrefix = 'ywE';

export const orgLicence = {
    None: 0,
    Trial: 5,
    Limited: 10,
    Unlimited: 100,
};

export const systemProgramProfiles = {
    Default: 'Default',
    Empty: 'Empty',
    All: 'All',
};

export const elementSettingKeys = {
    firstColWidth: '1st-col-width',
};

export const taskSettingKeys = {
    exportLabel: 'export-label',
};
